import { Component, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { InsuranceTableComponent } from '../../componentes/insurance-table/insurance-table.component';
import { ExportarComponent } from '../../componentes/exportar/exportar.component';
import { CamposComponent } from '../..//componentes/campos/campos.component';
import { TableConfig } from '../../interfaces/table-config.interface';
import {
  ComisionesRequest,
  PayStubResponse,
  CommissionDetailsResponse,
  CollectionCommissionResponse,
  TipoDocumentoComisiones,
} from '../../interfaces/comisiones.interface';
import { ApiResponse } from '../../interfaces/common.interfaces';
import { ComisionesService } from '../../services/comisiones.service';
import { AuthService } from '../../services/auth.service';
import {
  exportarDetallesPDF,
  exportarDetallesPorCobroPDF,
} from '../../helpers/exportarComisiones';
import { ComisionesFilter } from '../../interfaces/filters.interfaces';
import {
  mapComisionesDetalles,
  mapComisionesDetallesPorCobro,
} from '../../helpers/mapComisiones';
import { exportarVolantePago } from '../../helpers/exportarVolantePago';
import { HttpResponse } from '@angular/common/http';
import {
  paystubConfig,
  detailsConfig,
  collectionsConfig,
} from './components/helpers/tabConfig';
import { GoogleAnalyticsService } from '../../services/google-analytics/google-analytics.service';

/**
 * Componente para gestionar la visualización y exportación de comisiones
 */
@Component({
  selector: 'app-comisiones',
  standalone: true,
  imports: [
    InsuranceTableComponent,
    ExportarComponent,
    CamposComponent,
    CommonModule,
  ],
  templateUrl: './comisiones.component.html',
  styleUrl: './comisiones.component.css',
})
export class ComisionesComponent {
  insuranceData: any[] = [];
  errorStatus?: number;
  mensajeNoData: string = 'Selecciona los campos que deseas buscar';
  documentType: string = 'Volante de Pago';
  recievedData: any = {};
  usuario: string | null;
  route: string = '/comisiones';
  @ViewChild(InsuranceTableComponent) tablaComponent!: InsuranceTableComponent;
  title: string = '';
  tipoDocumento: number | undefined = undefined;
  fechaCorte: Date | undefined = undefined;
  tipoCorte: string = '';

  // src/app/paginas/comisiones/components/helpers/tabConfig.ts

  tableConfig?: TableConfig = {
    columns: [],
    showTotals: false,
    totalFields: [],
    showPagination: true,
  };

  /**
   * Constructor del componente
   * @param comisionesService Servicio para gestionar las comisiones
   * @param authService Servicio de autenticación
   */
  constructor(
    private comisionesService: ComisionesService,
    private authService: AuthService,
    private gAnalytics:GoogleAnalyticsService
  ) {
    this.title = 'Comisiones';
    this.usuario = this.authService.getProducerName();
  }

  /**
   * Maneja los cambios en los filtros y actualiza la tabla según el tipo de documento seleccionado
   * @param filters Filtros seleccionados por el usuario
   */
  onFilterChange(filters: ComisionesFilter): void {
    if (Object.keys(filters).length === 0) {
      this.insuranceData = [];
      this.mensajeNoData = 'Selecciona los campos que deseas buscar';
      this.errorStatus = undefined;
      return;
    }

    this.insuranceData = []; // Lmpieza de datos existentes;
    this.tableConfig = undefined; // Destrucción de la tabla

    const request: ComisionesRequest = {
      producerNumbers: this.authService.getProducerNumber() || 0,
      cutDate: filters.cutDate || new Date(),
      cutSelection: filters.cutSelection || 0,
    };

    this.tipoDocumento = filters.tipoDoc;
    this.fechaCorte = new Date(
      filters.cutDate!.setDate(filters.cutDate!.getDate() + 1)
    );
    this.tipoCorte =
      filters.cutSelection === 1
        ? 'Primer corte'
        : filters.cutSelection === 2
        ? 'Segundo corte'
        : 'Corte mesual';

    switch (filters.tipoDoc) {
      case TipoDocumentoComisiones.VOLANTE_PAGO:
        this.getPaystubData(request);
        break;

      case TipoDocumentoComisiones.DETALLES:
        this.getDetailsData(request);
        break;

      case TipoDocumentoComisiones.DETALLES_POR_COBRO:
        this.getCollectionsData(request);
        break;
    }
  }

  /**
   * Obtiene los datos del volante de pago
   * @param request Parámetros de la solicitud
   */
  private getPaystubData(request: ComisionesRequest) {
    this.comisionesService.getCommissionPaystub(request).subscribe({
      next: (response: HttpResponse<ApiResponse<PayStubResponse[]>>) => {
        if (response.status === 200) {
          this.insuranceData = Array.isArray(response.body?.data)
            ? response.body.data
            : [response.body?.data];
          this.mensajeNoData = '';
          this.errorStatus = undefined;
          // Asignación de tableConfig
          this.documentType = 'Volante de Pago';
          this.tableConfig = {
            ...paystubConfig,
            showPagination: true,
          };
        } else if (response.status === 204) {
          this.noDataResponse('No hay datos disponibles para mostrar');
        }
      },
      error: (error) => {
        console.error('Error al filtrar las pólizas:', error);
        this.noDataResponse(
          'Ha ocurrido un error en el servidor al obtener las comisiones'
        );
      },
    });
  }

  /**
   * Obtiene los detalles de las comisiones
   * @param request Parámetros de la solicitud
   */
  private getDetailsData(request: ComisionesRequest) {
    this.comisionesService.getCommissionDetails(request).subscribe({
      next: (
        response: HttpResponse<ApiResponse<CommissionDetailsResponse[]>>
      ) => {
        if (response.status === 200) {
          this.insuranceData = Array.isArray(response.body?.data)
            ? response.body.data
            : [response.body!.data];
          this.errorStatus = undefined;
          // Asignación de tableConfig
          this.documentType = 'Detalles';
          this.tableConfig = {
            ...detailsConfig,
            showPagination: true,
          };
        } else if (response.status === 204) {
          this.noDataResponse('No hay datos disponibles para mostrar');
        }
      },
      error: (error) => {
        console.error('Error al filtrar las pólizas:', error);
        this.noDataResponse(
          'Ha ocurrido un error en el servidor al obtener las comisiones'
        );
      },
    });
  }

  /**
   * Obtiene los datos de comisiones por cobro
   * @param request Parámetros de la solicitud
   */
  private getCollectionsData(request: ComisionesRequest) {
    this.comisionesService.getCommissionCollections(request).subscribe({
      next: (
        response: HttpResponse<ApiResponse<CollectionCommissionResponse[]>>
      ) => {
        if (response.status === 200) {
          this.insuranceData = Array.isArray(response.body?.data)
            ? response.body.data
            : [response.body!.data];
          this.errorStatus = undefined;
          // Asignación de tableConfig
          this.documentType = 'Detalles por Cobro';
          this.tableConfig = {
            ...collectionsConfig,
            showPagination: true,
          };
        } else if (response.status === 204) {
          this.noDataResponse('No hay datos disponibles para mostrar');
        }
      },
      error: (error) => {
        console.error('Error al filtrar las pólizas:', error);
        this.noDataResponse(
          'Ha ocurrido un error en el servidor al obtener las comisiones'
        );
      },
    });
  }

  /**
   * Establece el tipo de documento
   * @param type Tipo de documento a establecer
   */
  setDocumentType(type: string) {
    this.documentType = type;
  }

  /**
   * Maneja la exportación de datos según el formato y tipo de documento seleccionado
   * @param formato Formato de exportación (pdf u otros)
   */
  onExportar(formato: any): void {
    if (!this.insuranceData.length) return;

    if (formato === 'pdf') {
       //Evento para trackear las exportaciones de archivos en Google Analytics
      this.gAnalytics.trackEvent('descarga_comisiones',{
        page:'/comisiones',
        format:formato
      })
      if (this.tipoDocumento === 1) {
        // Documento Volante de pago
        const data = {
          data: this.insuranceData!,
          usuario: this.usuario!,
          fechaCorte: this.fechaCorte!,
          tipoCorte: this.tipoCorte,
        };
        
        exportarVolantePago(data);
      }
      if (this.tipoDocumento === 2) {
        // Documento Detalle de comisiones
        const mappeddata = mapComisionesDetalles(this.insuranceData);
        exportarDetallesPDF({
          data: mappeddata.data,
          currencyTotalCommission: mappeddata.currencyTotalCommission,
          currencyTotalPremiumNeta: mappeddata.currencyTotalPremiumNeta,
          usuario: this.usuario,
          fechaCorte: this.fechaCorte,
          tipoCorte: this.tipoCorte,
          productor: this.insuranceData[0].producerName,
        });
      }
      if (this.tipoDocumento === 3) {
        // Documento Detalle de comisiones por cobro
        const mappeddata = mapComisionesDetallesPorCobro(this.insuranceData);
        exportarDetallesPorCobroPDF({
          data: mappeddata.data,
          usuario: this.usuario,
          fechaCorte: this.fechaCorte,
          productor: this.insuranceData[0].producerName,
          primaNetaTotal: mappeddata.primaNetaTotal,
          participacionTotal: mappeddata.participacionTotal,
          comisionPrimaNetaTotal: mappeddata.comisionPrimaNetaTotal,
        });  
      }
    } else {
      if (this.tablaComponent) {
        const headerInfo = {
          titulo: this.title,
          fecha1: this.fechaCorte!,
          lob: '',
          slob: '',
          tableConfig: this.tableConfig!,
        };

        
      //Evento para trackear las exportaciones de archivos en Google Analytics
      this.gAnalytics.trackEvent('descarga_comisiones',{
        page:'/comisiones',
        format:formato
      })

        this.tablaComponent.exportarDatos(formato, headerInfo);
      }
    }
  }

  noDataResponse(message: string) {
    this.insuranceData = [];
    this.mensajeNoData = message;
    this.tableConfig = {
      columns: [],
      showTotals: false,
      totalFields: [],
      showPagination: true,
    };
  }

  @ViewChild(CamposComponent) camposComponent!: CamposComponent;
}
